import { spawn } from 'utils/translations';
import { CopyableCommand } from 'components/CopyableCommand';
import {
  GERRIT_WEBHOOK_EVENTS,
  IMPORT_PROGRESS_TRANSLATION_KEY,
} from '../consts';
import {
  buildCreateWebhookCommand,
  buildValidateWebhookCommand,
} from '../utils';

const spawnImport = spawn(IMPORT_PROGRESS_TRANSLATION_KEY);

const GERRIT_HOST_PLACEHOLDER = '<YOUR_GERRIT_HOST>';
const USERNAME_PLACEHOLDER = 'YOUR_USERNAME';

type WebhookSetupProps = {
  webhookUrl: string;
  gerritHost: string | undefined;
  username: string | undefined;
  /** Undefined falls back to the builders' default — the same `/a` the backend uses. */
  apiPrefix: string | undefined;
};

/**
 * Final-step panel: the copyable curls the user runs to register the Qodo
 * webhook on their Gerrit server's All-Projects (global) config, plus the
 * events it subscribes to. The webhook is always created manually — the portal
 * delegates to LiteGit and never registers it for Gerrit.
 */
export const WebhookSetup = ({
  webhookUrl,
  gerritHost,
  username,
  apiPrefix,
}: WebhookSetupProps) => {
  const host = gerritHost ?? GERRIT_HOST_PLACEHOLDER;
  const user = username ?? USERNAME_PLACEHOLDER;
  const createCommand = buildCreateWebhookCommand(
    host,
    webhookUrl,
    user,
    apiPrefix,
  );
  const validateCommand = buildValidateWebhookCommand(host, user, apiPrefix);
  const copyLabel = spawnImport('webhook-setup.copy-command-aria-label');

  return (
    <div className="flex flex-col gap-4 p-4 rounded-lg border border-border-bg-neutral">
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-text-general-neutral-strong">
          {spawnImport('webhook-setup.title')}
        </span>
        <span className="text-xs text-text-general-neutral-mild">
          {spawnImport('webhook-setup.description')}
        </span>
      </div>

      <p className="text-xs text-text-general-neutral-mid">
        {spawnImport('webhook-setup.steps-lead-in')}
      </p>

      <CopyableCommand
        label={spawnImport('webhook-setup.url-label')}
        command={webhookUrl}
        copyLabel={spawnImport('webhook-setup.copy-aria-label')}
        testId="gerrit-webhook-url"
      />
      <CopyableCommand
        label={spawnImport('webhook-setup.create-command-label')}
        command={createCommand}
        copyLabel={copyLabel}
      />
      <CopyableCommand
        label={spawnImport('webhook-setup.validate-command-label')}
        command={validateCommand}
        copyLabel={copyLabel}
      />

      <p className="text-xs text-text-general-neutral-mid">
        {spawnImport('webhook-setup.step-all-projects')}
      </p>

      <div className="flex flex-col gap-1.5">
        <span className="text-xs text-text-general-neutral-mid">
          {spawnImport('webhook-setup.step-events')}
        </span>
        <div className="flex flex-wrap gap-1.5">
          {GERRIT_WEBHOOK_EVENTS.map((event) => (
            <code
              key={event}
              className="px-1.5 py-0.5 rounded border border-border-bg-neutral bg-fill-interactive-neutral-idle text-text-general-neutral-strong"
            >
              {event}
            </code>
          ))}
        </div>
      </div>
    </div>
  );
};
